import type { CalendarioEvento, CalendarioParticipando } from '@/hooks/useCalendario';
import { dateKey, buildGridDays } from '@/lib/calendario';
import { ETAPA_LABELS } from '@/lib/pipelineKanban';

interface CalendarioGridProps {
  year: number;
  month: number;
  vinculadas: CalendarioEvento[];
  participando: CalendarioParticipando[];
  onDiaClick: (dateKey: string) => void;
  onEventoClick: (evento: CalendarioEvento) => void;
  onParticipandoClick: (item: CalendarioParticipando) => void;
}

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function CalendarioGrid({ year, month, vinculadas, participando, onDiaClick, onEventoClick, onParticipandoClick }: CalendarioGridProps) {
  const days = buildGridDays(year, month);
  const today = dateKey(new Date());

  const vinculadasPorDia = new Map<string, CalendarioEvento[]>();
  vinculadas.forEach((evento) => {
    const key = dateKey(new Date(evento.dataEvento));
    if (!vinculadasPorDia.has(key)) vinculadasPorDia.set(key, []);
    vinculadasPorDia.get(key)?.push(evento);
  });

  const participandoPorDia = new Map<string, CalendarioParticipando[]>();
  participando.forEach((item) => {
    if (!item.licitacao.prazoAbertura) return;
    const key = dateKey(new Date(item.licitacao.prazoAbertura));
    if (!participandoPorDia.has(key)) participandoPorDia.set(key, []);
    participandoPorDia.get(key)?.push(item);
  });

  return (
    <div className="rounded-xl border border-ds-border overflow-hidden">
      <div className="grid grid-cols-7 bg-ds-surface2 text-xs font-medium text-ds-text-secondary">
        {DIAS_SEMANA.map((dia) => (
          <div key={dia} className="px-2 py-2 text-center">{dia}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = dateKey(day);
          const isCurrentMonth = day.getMonth() === month;
          const eventosVinculados = vinculadasPorDia.get(key) || [];
          const eventosParticipando = participandoPorDia.get(key) || [];
          const isToday = key === today;

          return (
            <div
              key={key}
              className={`min-h-[96px] border-t border-l border-ds-border p-1.5 flex flex-col gap-1 ${
                isCurrentMonth ? 'bg-ds-surface' : 'bg-ds-surface2/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs ${isCurrentMonth ? 'text-ds-text-primary' : 'text-ds-text-secondary/60'} ${
                    isToday ? 'font-bold text-ds-success' : ''
                  }`}
                >
                  {day.getDate()}
                </span>
                <button
                  type="button"
                  aria-label={`Vincular licitação em ${key}`}
                  onClick={() => onDiaClick(key)}
                  className="text-ds-text-secondary hover:text-ds-success text-xs leading-none px-1"
                >
                  +
                </button>
              </div>

              <div className="flex flex-col gap-0.5 overflow-y-auto max-h-20">
                {eventosVinculados.map((evento) => (
                  <button
                    key={`v-${evento.id}`}
                    type="button"
                    title={`${evento.empresaNome} — ${evento.licitacao.objeto}`}
                    onClick={() => onEventoClick(evento)}
                    className="text-left text-[10px] leading-tight px-1.5 py-0.5 rounded bg-ds-info/15 text-ds-info hover:bg-ds-info/25"
                  >
                    <span className="block font-semibold truncate">{evento.empresaNome}</span>
                    <span className="block truncate">{evento.licitacao.objeto}</span>
                  </button>
                ))}

                {eventosParticipando.map((item) => (
                  <button
                    key={`p-${item.id}`}
                    type="button"
                    title={`${item.empresaNome} — ${item.licitacao.objeto} (${(ETAPA_LABELS as Record<string, string>)[item.etapa] || item.etapa})`}
                    onClick={() => onParticipandoClick(item)}
                    className="text-left text-[10px] leading-tight px-1.5 py-0.5 rounded bg-ds-warning/15 text-ds-warning hover:bg-ds-warning/25"
                  >
                    <span className="block font-semibold truncate">{item.empresaNome}</span>
                    <span className="block truncate">{item.licitacao.objeto}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { CalendarioGrid };
